import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy, Share2 } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
} from '@mlm/ui';
import {
  getMe,
  getPackageLinks,
  getReferralCode,
  getReplicatedPage,
  getSlotShareLinks,
  getTenantConfig,
  listReplicatedPages,
} from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useMySlots } from '../lib/hooks/use-my-slots';
import { DownloadableQr } from '../components/downloadable-qr';

// Superseded by Campaign Links — flip to restore the "Package links" card.
// Server-side scoping (resolveLinkKitScope) is unaffected by this hide.
const SHOW_PACKAGE_LINKS = false;

export function InviteFriendsPage() {
  const storeUser = useAuthStore((s) => s.user);
  const [copied, setCopied] = useState(false);

  // Re-fetch /me on mount so the referral code is correct on first login even
  // if the in-memory session was hydrated before it was available — no manual
  // page refresh needed. Falls back to the store while loading.
  const meQuery = useQuery({ queryKey: ['me'], queryFn: getMe, staleTime: 60_000 });
  const referralCode = meQuery.data?.referralCode ?? storeUser?.referralCode ?? null;

  // The value to embed in the link honors `replicated_link_ref_encrypted`:
  // `shareRef` is the encrypted opaque token when on, else the plaintext code.
  const refQuery = useQuery({ queryKey: ['referral-code'], queryFn: getReferralCode, staleTime: 60_000 });

  // A multi-slot member shares a per-SLOT link so the slot they're actively
  // working gets credited. Resolve the active slot's own shareRef/code and fall
  // back to the account-level referral code (free slots / single-code tenants /
  // not-yet-loaded keep the page working exactly as before).
  const { activeSlot } = useMySlots();
  const slotLinksQuery = useQuery({
    queryKey: ['slot-share-links'],
    queryFn: getSlotShareLinks,
    staleTime: 60_000,
  });
  const activeLink = slotLinksQuery.data?.find((l) => l.slotId === activeSlot?.id) ?? null;
  const shareRef = activeLink?.shareRef ?? refQuery.data?.shareRef ?? referralCode;
  const displayCode = activeLink?.customCode ?? activeLink?.slotCode ?? referralCode;

  // When the tenant enabled the custom replicated landing page, the link points
  // at `/r/:code` (which renders the page, then routes to signup carrying the
  // code); otherwise it's the legacy straight-to-signup link.
  const replicatedQuery = useQuery({ queryKey: ['replicated-page'], queryFn: () => getReplicatedPage() });
  const useCustomPage = replicatedQuery.data?.enabled ?? false;

  // The tenant's named campaign pages (each shareable as /r/<ref>/<slug>).
  const pagesQuery = useQuery({
    queryKey: ['replicated-pages'],
    queryFn: listReplicatedPages,
    staleTime: 60_000,
  });
  const campaignPages = pagesQuery.data?.pages ?? [];

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const link = useMemo(() => {
    if (!shareRef) return '';
    return useCustomPage
      ? `${origin}/r/${encodeURIComponent(shareRef)}`
      : `${origin}/signup?ref=${encodeURIComponent(shareRef)}`;
  }, [shareRef, useCustomPage, origin]);
  const showCampaigns = useCustomPage && !!shareRef && campaignPages.length > 0;

  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore */
    }
  }

  // Replicated-link packages: the activation kits the member's membership tags
  // for its replicated link. Scope is now membership-derived (one `?ref=` link,
  // no per-package URL), so the card just lists which packages that single link
  // unlocks for a lead. Gated by the tenant `packageReplicatedLinkEnabled` flag.
  const tenant = useQuery({ queryKey: ['tenant-config'], queryFn: getTenantConfig });
  // NOTE: stays enabled even while the Package Links card is hidden — the
  // Campaign links card annotates each tagged campaign with the member's
  // matching packages from this same query (an empty list would falsely render
  // "No matching package for your tier" on every tagged campaign).
  const packageLinksQuery = useQuery({
    queryKey: ['package-links'],
    queryFn: getPackageLinks,
    staleTime: 60_000,
  });
  const packages = packageLinksQuery.data?.packages ?? [];
  const showPackages =
    SHOW_PACKAGE_LINKS && (tenant.data?.packageReplicatedLinkEnabled ?? true) && packages.length > 0;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-h1 flex items-center gap-2">
          <Share2 className="h-6 w-6 text-primary" /> Invite Friends
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Share your replicated link. Anyone who signs up through it becomes your affiliate.
        </p>
      </div>

      {!referralCode ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            {meQuery.isLoading
              ? 'Loading your replicated link…'
              : "Your referral code hasn't been generated yet."}
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Your replicated link</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input readOnly value={link} className="font-mono text-xs" />
                <Button onClick={copy} variant="outline" size="sm">
                  {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <div className="text-xs text-muted-foreground">
                Affiliate Code: <span className="font-mono">{displayCode}</span>
                <Link
                  to={
                    activeSlot?.id
                      ? `/settings?tab=referral&slot=${activeSlot.id}`
                      : '/settings?tab=referral'
                  }
                  className="ml-2 text-primary hover:underline"
                >
                  Change
                </Link>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">QR code</CardTitle>
            </CardHeader>
            <CardContent>
              <DownloadableQr
                value={link}
                fileName={`referral-${displayCode ?? 'link'}`}
                caption="Scan with a phone camera to open the signup page."
              />
            </CardContent>
          </Card>

          {showCampaigns && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Campaign links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Share a specific landing page. Every link still credits your affiliate network.
                </p>
                <ul className="space-y-2">
                  {campaignPages.map((p) => (
                    <CampaignRow
                      key={p.slug}
                      title={p.title}
                      isDefault={p.isDefault}
                      link={`${origin}/r/${encodeURIComponent(shareRef!)}/${encodeURIComponent(p.slug)}`}
                      // Which of the member's packages this campaign unlocks. An
                      // untagged campaign ([] ) leaves the whole tier list.
                      packageNames={
                        p.kitProductIds.length === 0
                          ? null
                          : packages
                              .filter((pkg) => p.kitProductIds.includes(pkg.kitProductId))
                              .map((pkg) => pkg.kitName)
                      }
                    />
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {showPackages && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Package links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Anyone who signs up through your replicated link above can activate their first
                  slot with any of these packages:
                </p>
                <ul className="space-y-2">
                  {packages.map((pkg) => (
                    <li
                      key={pkg.kitProductId}
                      className="flex items-center justify-between rounded-md border p-3 text-sm"
                    >
                      <span className="font-medium">{pkg.kitName}</span>
                      {pkg.membershipName && (
                        <span className="text-xs text-muted-foreground">{pkg.membershipName}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

/** One campaign page's shareable link with its own copy button. */
function CampaignRow({
  title,
  isDefault,
  link,
  packageNames,
}: {
  title: string;
  isDefault: boolean;
  link: string;
  // null = campaign is untagged (unlocks all the member's packages);
  // [] = tagged but none overlap the member's tier (lead can buy nothing).
  packageNames: string[] | null;
}) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore */
    }
  }
  return (
    <li className="space-y-1 rounded-md border p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {title}
          {isDefault && (
            <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
              Default
            </span>
          )}
        </span>
        <Button onClick={copy} variant="outline" size="sm">
          {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <Input readOnly value={link} className="font-mono text-xs" />
      <p className="text-[11px] text-muted-foreground">
        {packageNames === null
          ? 'Unlocks all your packages'
          : packageNames.length
            ? `Package${packageNames.length > 1 ? 's' : ''}: ${packageNames.join(', ')}`
            : 'No matching package for your tier'}
      </p>
    </li>
  );
}
